import React, { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useLocation } from 'react-router-dom';
import { ApplicationContext } from '../../context/GlobalState';
import { t, extractLocaleFromPath, localizedPath } from '../../i18n';
import HreflangTags from '../../components/HreflangTags/HreflangTags';
import { buildServicePageSchema } from '../../utils/schemaMarkup';
import CTABanner from '../../components/CTABanner/CTABanner';
import taxonomy from '../../data/taxonomy.json';
import './LegalIntent.css';

export default function LegalIntent() {
  const { intentSlug } = useParams();
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand || state.client;

  const intent = taxonomy.legalIntentPages?.find((p) => p.slug === intentSlug);

  if (!intent) {
    return (
      <div className="container section-padding">
        <h1>Page Not Found</h1>
        <p>The legal services page you're looking for doesn't exist.</p>
      </div>
    );
  }

  const pageTitle = `${intent.display} | ${brand?.name || 'Georgia Spine & Orthopaedics'}`;
  const metaDescription = `${intent.description} Contact ${brand?.name} for a free consultation. Call ${brand?.phone} today.`;
  const canonicalUrl = `https://${brand?.domain || 'gaspineortho.com'}/${locale}/legal/${intentSlug}`;
  const baseDomain = brand?.domain || 'gaspineortho.com';
  const legalSchema = buildServicePageSchema({
    brand,
    service: { display: intent.display, slug: `legal/${intentSlug}`, category: 'Legal Services' },
    location: { city: brand?.city || '', state: brand?.state || 'GA', slug: 'legal', address: '' },
  });

  return (
    <div className="legal-intent-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        {locale !== 'en' && <html lang={locale} />}
      </Helmet>

      <HreflangTags baseDomain={baseDomain} pathWithoutLocale={`/legal/${intentSlug}`} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(legalSchema) }}
      />

      {/* Hero */}
      <section className="glass-dark legal-hero" style={{ padding: '80px 0', textAlign: 'center', borderRadius: 0, marginBottom: 0 }}>
        <div className="container">
          <div
            className="glass-badge"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', marginBottom: 16, display: 'inline-block', padding: '4px 16px', borderRadius: 20 }}
          >
            Legal Services &amp; Referrals
          </div>
          <h1 style={{ fontSize: '2.6rem', fontWeight: 800, color: '#fff', marginBottom: 16 }}>
            {intent.display}
          </h1>
          <p style={{ opacity: 0.9, maxWidth: 640, margin: '0 auto 28px', color: '#fff', fontSize: '1.1rem', lineHeight: 1.7 }}>
            {intent.description}
          </p>
          <a href={localizedPath('/request-appt', locale)} className="glass-btn glass-btn-accent" style={{ marginRight: 12 }}>
            {t('legal.requestAppt', locale)}
          </a>
          <a href={`tel:${brand?.phone?.replace(/\D/g, '')}`} className="glass-btn" style={{ border: '2px solid rgba(255,255,255,0.6)', color: '#fff' }}>
            {t('legal.call', locale, { phone: brand?.phone })}
          </a>
        </div>
      </section>

      <div className="container" style={{ maxWidth: 960, margin: '0 auto', padding: '40px 20px 60px' }}>

        {/* How It Works */}
        <div className="glass-panel legal-section" style={{ padding: 32, marginBottom: 24 }}>
          <h2 style={{ marginBottom: 16 }}>{t('legal.howItWorks', locale)}</h2>
          <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.8, marginBottom: 16 }}>
            {intent.referralInfo}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, marginTop: 24 }}>
            {[
              { step: '1', label: t('legal.step1', locale), icon: '📞' },
              { step: '2', label: t('legal.step2', locale), icon: '🩺' },
              { step: '3', label: t('legal.step3', locale), icon: '🏥' },
              { step: '4', label: t('legal.step4', locale), icon: '📋' },
            ].map(({ step, label, icon }) => (
              <div
                key={step}
                className="glass-panel"
                style={{ padding: '20px 16px', textAlign: 'center', borderRadius: 10 }}
              >
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>{icon}</div>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--color-accent)', marginBottom: 4 }}>
                  {t('legal.step', locale, { step })}
                </div>
                <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* When to Seek Legal Help */}
        <div className="glass-panel legal-section" style={{ padding: 32, marginBottom: 24 }}>
          <h2 style={{ marginBottom: 16 }}>{t('legal.whenToSeek', locale)}</h2>
          <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.8, marginBottom: 12 }}>
            {t('legal.whenToSeekIntro', locale)}
          </p>
          <ul style={{ color: 'var(--color-text-secondary)', lineHeight: 2, paddingLeft: 20 }}>
            <li>{t('legal.seekItem1', locale)}</li>
            <li>{t('legal.seekItem2', locale)}</li>
            <li>{t('legal.seekItem3', locale)}</li>
            <li>{t('legal.seekItem4', locale)}</li>
            <li>{t('legal.seekItem5', locale)}</li>
          </ul>
        </div>

        {/* Attorney Referral Note */}
        <div className="glass-panel legal-section" style={{ padding: 32, marginBottom: 24, borderLeft: '4px solid var(--color-accent)' }}>
          <h2 style={{ marginBottom: 12 }}>{t('legal.referralTitle', locale)}</h2>
          <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.8 }}>
            {t('legal.referralDesc', locale, { brandName: brand?.name, state: brand?.state || 'Georgia' })}
            {' '}{t('privacy.contactDesc', locale, { phone: brand?.phone, email: brand?.email })}
          </p>
          <div style={{ marginTop: 20 }}>
            <a href={localizedPath('/resources/patient-referral', locale)} className="glass-btn glass-btn-primary" style={{ marginRight: 12 }}>
              {t('legal.referralPortal', locale)}
            </a>
            <a href={`tel:${brand?.phone?.replace(/\D/g, '')}`} className="glass-btn">
              {t('legal.call', locale, { phone: brand?.phone })}
            </a>
          </div>
        </div>

      </div>

      <CTABanner client={brand} />
    </div>
  );
}
